
import AppBrowse from "@/components/browse/Browse.vue";
import {Purchase} from "@/models/base";
import { BrowseService } from "@/services/browse-service";
import { PurchaseService } from "@/services/purchase-service";
import { ToolbarService } from "@/services/toolbar-service";
import { IBrowseDataSource } from "@/utils/browse/browse-data-source";
import Vue from "vue";
import { UserConfigService } from "../../services/user-config-service";
import { checkHasModule } from "../../router";
import { MODULE } from "../../config/modules";
import { BrowseState } from "@/utils/browse/browse-state";
import AppBankEdit from "@/components/bank/QuickEdit.vue";
import { ISelectItem } from "winfakt-vue-components/src/classes";
import AppPurchaseExportToIsabel from "@/components/purchases/PurchaseExportToIsabel.vue";
import { AuthService } from "@/services/auth-service";
import { FetchedTotal } from "@/utils/views/fetched-total";
import { View } from "@/models/view";
import { downloadSelectionAsCSV } from "@/utils/csv-export";
import { UblService } from "@/plugins/ubl/services/ubl-service";
import AppCreateUblArchiveModal from "@/components/settings-default/CreateUblArchiveModal.vue";
import {JobService} from "@/services/job-service";
import { DossierConfigService, MainSocketService } from "@/services";
import AppDeletedPurchasesBrowse from "@/components/purchases/DeletedPurchasesBrowse.vue";
import { PurchaseAwsFile } from "@/models/base/purchase-aws-file";
import AppUblToPurchaseModal from "@/components/purchases/UblToPurchaseModal.vue";

export default Vue.extend({
	data(){
		return {
			quickBankEdit: false,
			quickBankEditPurchase: null as null | Purchase,
			purchaseState: null as null | BrowseState,
			selectedItems: [] as Purchase[],
			exportToIsabelModal: false,
			createUblArchiveModal: false,
			ublArchiveSelectedIds: [] as number[],
			JobService,
			deletedPurchasesModal: false,
			ublToPurchaseModal: false,
		};
	},
	props: {
		browseId: Number,
	},
	computed: {
		// eslint-disable-next-line max-lines-per-function
		dataSource(): IBrowseDataSource<Purchase> {
			let elem = this;
			return {
				getFullElement: (el:Purchase)=>PurchaseService.getPurchase(el.ID),
				getSettings: () => BrowseService.getBrowse(this.browseId),
				getTotals(view:View):Promise<FetchedTotal[]>{
					return PurchaseService.getTotals(view);
				},
				getElements: PurchaseService.getPurchases.bind(PurchaseService),
				edit(purchase: Purchase) {
					elem.$router.push({path: `/purchase/edit/${purchase.ID}`});
				},
				canEdit(p:Purchase):boolean{
					if (p.ComputedFriendlyID.startsWith("-")){
						return false;
					}
					return true;
				},
				async delete(purchases: Purchase[]) {
					let haveBanks = await PurchaseService.purchasesHaveBanks(purchases);
					if (haveBanks) {
						let confirm = await elem.$confirmBuilder(elem.$ct("confirm-delete-with-payments")).setWarningTitle().show();
						if (!confirm) {
							return false;
						}
					}
					await PurchaseService.delPurchases(purchases);
					return true;
				},
				addRoute: `/purchase/new`,
				info(purchase:Purchase){
					elem.$router.push({path: `/purchase/info/${purchase.ID}`});
				},
				browseName: "Purchases",
				modelName: "Purchase",
				browsePermissions: ()=>UserConfigService.getPermissionSettings().PurchasePermissions,
				getOnElementUpdateSocketEvent: (item:Purchase)=>`/purchase/${item.ID}/update`,
				onSocketElementUpdated: (data:any)=>new Purchase(data),
				getDocumentsDataSource: (v:Purchase)=>PurchaseAwsFile.getDataSource(v),
				extraFieldSettings: {
					getExtraFields: PurchaseService.getExtraFields.bind(PurchaseService),
					nameSettings: DossierConfigService.getExtraFieldNamesConfigs().PurchaseNames,
					batchEdit: PurchaseService.batchEditExtraField.bind(PurchaseService),
					edit: (PurchaseService.updateExtraFields as any).bind(PurchaseService)
				}
			};
		},
		canDownloadDocuments():boolean {
			for (let item of this.selectedItems) {
				if (item.HasAttachedDocument) {
					return true;
				}
			}
			return false;
		},
		shouldDisableSendPurchaseUbl():boolean {
			return this.selectedItems.length == 0 && !AuthService.hasModule(MODULE.EXPORT_NAAR_EXTERN_BOEKHPAKKET);
		},
		batchSendToBookkeeperItem():ISelectItem<Function>{
			return {
				text: this.$ctc("email-purchase-to-bookkeeper", this.selectedItems.length),
				value: this.batchSendToBookkeeper,
				icon: "user-tie",
				disabled: this.shouldDisableSendPurchaseUbl
			};
		},
		// eslint-disable-next-line max-lines-per-function
		buttonBarItems():ISelectItem<string | Function>[] {
			let result:ISelectItem<string | Function>[] = [];
			result.push({
					text: this.$ct("create-ubl-archive-for-accountant"),
					value: "createUblArchive",
					icon: "archive",
					disabled: !AuthService.hasModule(MODULE.EXPORT_NAAR_EXTERN_BOEKHPAKKET)
				},
				this.batchSendToBookkeeperItem,
				{
					text: this.$ct("fill-purchase-pdf-document-indexes"),
					value: "fillDocumentIndexes",
					icon: "list",
					disabled: this.isFillingDocumentIndexesRunning
				},
			);
			if (AuthService.isDevModeEnabled){
				result.push({
					text: "Verwijderde documenten bekijken",
					value: "openDeletedPurchasesModal",
					icon: "ban"
				});
			}
			result.push({
				text: this.$ct("ubl-to-purchase"),
				value: this.openUblToPurchaseModal,
				icon: "upload",
				disabled: !AuthService.hasModule(MODULE.ELECTRONISCHE_FACTURATIE)
			});
			if (this.selectedItems.length == 0) {
				return result;
			}
			result.unshift({text: this.$ct("process-payment"), value: "processPayment", icon: "euro-sign"});
			result.push({
				text: this.$ct("export-to-isabel"),
				value: "exportToIsabel",
				icon: "file-export",
				disabled: !AuthService.hasModule(MODULE.LINK_MET_ISABEL)
			});
			return result;
		},
		firstSelectedItem(): Purchase {
			return this.selectedItems[0];
		},
		isFillingDocumentIndexesRunning():boolean {
			return JobService.getJobs().findIndex(j=>j.Name == "fill-purchase-pdf-document-indexes") != -1;
		},
		contextMenuItems():ISelectItem<string | Function>[]{
			let items:ISelectItem<string | Function>[] = [
				{
					text: this.$ct("view-purchase"),
					value: "viewPurchase",
					icon: "eye"
				},
				{
					text: this.$ct("process-payment"),
					value: "processPayment",
					icon: "euro-sign"
				},
				{
					text: this.$ct("download-document"),
					value: "downloadDocument",
					icon: "download"
				},
				{
					text: this.$ct("export-to-isabel"),
					value: "exportToIsabel",
					icon: "file-export",
					disabled: !AuthService.hasModule(MODULE.LINK_MET_ISABEL)
				},
				{
					text: this.$ct("export-to-csv"),
					value: "exportAsCsv",
					icon: "download",
					disabled: !AuthService.hasModule(MODULE.LINK_MET_ISABEL)
				},
				{
					text: this.$ct("download-ubl"),
					value: "downloadUbl",
					icon: "download",
					disabled: !AuthService.hasModule(MODULE.ELECTRONISCHE_FACTURATIE)
				},
				this.batchSendToBookkeeperItem
			];
			return items;
		},

	},
	watch: {
		"$route.path": {
			immediate: true,
			handler(){
				if (this.$route.path == "/purchase"){
					ToolbarService.title = this.$ct("purchases");;
				}
			}
		}
	},
	methods: {
		async batchSendToBookkeeper(){
			if (this.selectedItems.length == 0) return;
			await PurchaseService.batchSendToBookkeeper(this.selectedItems);
		},
		async onExportToIsabelComplete(){
			this.purchaseState?.emptySelectedItems();
			await this.purchaseState?.fetch();
		},
		openUblToPurchaseModal(){
			this.ublToPurchaseModal = true;
		},
		async createUblArchive() {
			this.ublArchiveSelectedIds = this.selectedItems.map(p=>p.ID);
			this.createUblArchiveModal = true;
		},
		getSelectedItem(item?:Purchase):Purchase{
			if (item){
				return item;
			}
			if (this.firstSelectedItem){
				return this.firstSelectedItem;
			}
			throw new Error("No item selected");
		},
		async downloadDocument(item:Purchase){
			await item.downloadFiles();
		},
		async downloadDocuments() {
			for (let item of this.selectedItems) {
				await item.downloadFiles();
			}
		},
		viewPurchase(item?:Purchase){
			console.log("item",item);
			if(!item) return;
			this.$router.push({path: `/purchase/edit/${item.ID}`});
		},
		processPayment(item?:Purchase) {
			item = this.getSelectedItem(item);
			this.quickBankEditPurchase = item;
			this.quickBankEdit = true;
		},
		async downloadUbl(item?:Purchase){
			item = this.getSelectedItem(item);
			await UblService.downloadPurchaseUbl(this, item);
		},
		onUpdateBank(){
			if (!this.purchaseState) return;
			this.purchaseState.fetch();
			this.quickBankEdit = false;
		},
		async updateView() {
			if (!this.purchaseState) return;
			await this.purchaseState.fetch();
		},
		cancelSelection(){
			if (!this.purchaseState) return;
			this.purchaseState.emptySelectedItems();
		},
		exportToIsabel(purchase:Purchase){
			console.log(purchase);
			if (this.selectedItems.length == 0) {
				this.selectedItems.push(purchase);
			}
			this.exportToIsabelModal = true;
		},
		exportAsCsv(){
			console.log(this.purchaseState?.querySettings.view.Columns);
			const columns =  this.purchaseState?.querySettings.view.Columns || [];
			downloadSelectionAsCSV(this.selectedItems,"purchase-export", columns);
		},
		async fillDocumentIndexes(){
			await PurchaseService.fillDocumentIndexes();
			this.$wf.notify(this.$ct("indexes-are-being-filled"), "info", 3000, "info-circle");
		},
		openDeletedPurchasesModal(){
			this.deletedPurchasesModal = true;
		},
	},
	mounted() {
		ToolbarService.title = this.$ct("purchases");
	},
	async created(){
		if (!checkHasModule(MODULE.AANKOOPFACTUREN, this.$router)){
			throw new Error("MISSING MODULE");
		}
	},
	destroyed(){
		MainSocketService.offAll(this);
	},
	components: {
		AppBrowse,
		AppBankEdit,
		AppPurchaseExportToIsabel,
		AppCreateUblArchiveModal,
		AppDeletedPurchasesBrowse,
		AppUblToPurchaseModal
	},
	i18nEx: {
		componentPrefix: "components.purchases"
	},
});
